import { useState, useEffect } from "react";
import { fetchClients as fetchClientsApi, getClient, deleteClient, type Client as ApiClient, type ClientListItem } from "@/lib/clientsApi";
import {
  fetchLegacyClients as fetchLegacyClientsApi,
  getLegacyClient,
  deleteLegacyClient,
  type LegacyClient as LegacyApiClient,
  type LegacyClientListItem,
} from "@/lib/legacyClientsApi";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/useDebounce";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Search, Mail, Phone, ChevronLeft, ChevronRight, ArrowUpDown, Trash2 } from "lucide-react";
import ClientForm from "./ClientForm";
import { useAuth } from "@/contexts/AuthContext";

type Client = ClientListItem | LegacyClientListItem;

type SortField = "name" | null;
type SortOrder = "asc" | "desc";

interface ClientListProps {
  variant?: "default" | "legacy";
}

const ClientList = ({ variant = "default" }: ClientListProps) => {
  const isLegacyVariant = variant === "legacy";
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState<ApiClient | LegacyApiClient | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  
  const { toast } = useToast();
  const { userRole } = useAuth();

  const canEdit = userRole === "ceo" || userRole === "general_manager" || userRole === "sales_staff";
  const canDelete = userRole === "ceo" || userRole === "general_manager";

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    loadClients();
  }, [currentPage, pageSize, sortField, sortOrder, debouncedSearchTerm, isLegacyVariant]);

  const loadClients = async () => {
    try {
      setIsLoading(true);

      const response = isLegacyVariant
        ? await fetchLegacyClientsApi({
            page: currentPage,
            size: pageSize,
            search: debouncedSearchTerm,
            sortField: sortField ?? undefined,
            sortOrder,
          })
        : await fetchClientsApi({
            page: currentPage,
            size: pageSize,
            search: debouncedSearchTerm,
            sortField: sortField ?? undefined,
            sortOrder,
          });

      const items = Array.isArray(response?.items) ? response.items : [];
      const total = typeof response?.total === "number" ? response.total : items.length;
      setClients(items);
      setTotalCount(total);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRowClick = (client: Client) => {
    if (isLegacyVariant) {
      handleOpenClient(client, true);
      return;
    }
    if (canEdit) {
      handleOpenClient(client, false);
    }
  };

  const handleOpenClient = async (client: Client, viewMode: boolean) => {
    try {
      const data = isLegacyVariant
        ? await getLegacyClient(client.id)
        : await getClient(client.id);
      setSelectedClient(data as any);
      setIsViewMode(viewMode);
      setIsFormOpen(true);
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };


  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortField(null);
        setSortOrder("asc");
      }
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  const handleFormSuccess = () => {
    loadClients();
    setIsFormOpen(false);
    setSelectedClient(null);
    setIsViewMode(false);
  };

  const handleDelete = async (clientId: number) => {
    if (!canDelete) return;
    const confirmed = window.confirm("Delete this client? This action cannot be undone.");
    if (!confirmed) return;

    try {
      if (isLegacyVariant) {
        await deleteLegacyClient(clientId);
      } else {
        await deleteClient(clientId);
      }
      toast({
        title: "Client deleted",
        description: "Client removed successfully.",
      });
      await loadClients();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getLanguageLabel = (lang: string) => {
    return lang === "en" ? "English" : "PT-BR";
  };

  const getCurrencyLabel = (curr: string | null) => {
    return curr ? curr.toUpperCase() : "";
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const showCurrencyColumn = isLegacyVariant;
  const columnCount = 3 + (showCurrencyColumn ? 1 : 0) + (canDelete ? 1 : 0);

  return (
    <div className="space-y-4">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by name, email, or company..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="pl-10"
        />
      </div>

      <div className="rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2"
                  onClick={() => handleSort("name")}
                >
                  Name
                  <ArrowUpDown 
                    className={`ml-2 h-4 w-4 ${sortField === "name" ? "text-primary" : ""}`} 
                  />
                </Button>
              </TableHead>
              <TableHead>
                Contact
              </TableHead>
              <TableHead>Language</TableHead>
              {showCurrencyColumn && <TableHead>Currency</TableHead>}
              {canDelete && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="text-center text-muted-foreground h-24">
                  Loading clients...
                </TableCell>
              </TableRow>
            ) : clients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="text-center text-muted-foreground">
                  No clients found
                </TableCell>
              </TableRow>
            ) : (
              clients.map((client) => (
                <TableRow 
                  key={client.id}
                  className={isLegacyVariant || canEdit ? "cursor-pointer hover:bg-muted/50" : ""}
                  onClick={() => handleRowClick(client)}
                >
                  <TableCell>
                    <div>
                      <div className="font-medium">
                        {client.first_name} {client.last_name}
                      </div>
                      {client.company && (
                        <div className="text-sm text-muted-foreground">
                          {client.company}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {client.email && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="h-3 w-3 text-muted-foreground" />
                          {client.email}
                        </div>
                      )}
                      {client.mobile && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="h-3 w-3 text-muted-foreground" />
                          {client.mobile}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{getLanguageLabel(client.language)}</TableCell>
                  {showCurrencyColumn && (
                    <TableCell>{getCurrencyLabel((client as any).currency ?? null)}</TableCell>
                  )}
                  {canDelete && (
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(client.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-sm text-muted-foreground">
            Showing {clients.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
            {Math.min(currentPage * pageSize, totalCount)} of {totalCount} records
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1 || isLoading}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-sm">
            Page {currentPage} of {totalPages || 1}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage >= totalPages || isLoading}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <ClientForm
        open={isFormOpen}
        onOpenChange={(open) => {
          setIsFormOpen(open);
          if (!open) {
            setSelectedClient(null);
            setIsViewMode(false);
          }
        }}
        onSuccess={handleFormSuccess}
        initialData={selectedClient}
        variant={isLegacyVariant ? "legacy" : "default"}
        readOnly={isViewMode}
      />
    </div>
  );
};

export default ClientList;
